import { TILE_SIZE } from '@/sprites/tiles';
import { bossFor } from '@/data/bosses';
import { FLOOR, STAIRS_DOWN, STAIRS_UP, WALL } from '@/dungeon/generator';
import { Area, type AreaDefinition } from '@/world/area';

/**
 * A boss floor.
 *
 * Hand-shaped rather than generated, and the only floor in the game that is.
 * A generated arena would be a room that happened to be big; this one is
 * *composed* — one space, the way in behind you, the way on across it, and
 * nothing else to read. Everything a generated floor is good at (surprise,
 * variety, a route to pick) is exactly wrong when the thing to pay attention
 * to is in the middle of the room.
 *
 * Every tenth floor. Deep enough that arriving is an achievement, shallow
 * enough that it is not the end of the dungeon.
 */

const WIDTH = 27;
const HEIGHT = 21;

export function isBossDepth(depth: number): boolean {
  return bossFor(depth) !== null;
}

export function makeArenaArea(depth: number): Area {
  const rows: string[] = [];

  for (let y = 0; y < HEIGHT; y++) {
    let row = '';
    for (let x = 0; x < WIDTH; x++) {
      const edge = x === 0 || y === 0 || x === WIDTH - 1 || y === HEIGHT - 1;
      row += edge ? WALL : FLOOR;
    }
    rows.push(row);
  }

  const midY = Math.floor(HEIGHT / 2);
  const set = (x: number, y: number, char: string): void => {
    rows[y] = rows[y]!.slice(0, x) + char + rows[y]!.slice(x + 1);
  };

  // The way back, and the way on. Opposite ends, so crossing the room is the
  // fight rather than something you can walk around.
  set(2, midY, STAIRS_UP);
  set(WIDTH - 3, midY, STAIRS_DOWN);

  const definition: AreaDefinition = {
    id: 'dungeon',
    name: `${bossFor(depth)?.name ?? 'The Deep'} — Floor ${depth}`,
    rows,
    spawn: { x: 2 * TILE_SIZE, y: midY * TILE_SIZE },
    portals: [],
    indoor: true,
  };

  return new Area(definition);
}

/** Where the boss waits: the middle of the room, in world pixels. */
export function arenaCentre(): { x: number; y: number } {
  return {
    x: Math.floor(WIDTH / 2) * TILE_SIZE + TILE_SIZE / 2,
    y: Math.floor(HEIGHT / 2) * TILE_SIZE + TILE_SIZE / 2,
  };
}
